import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Users, CreditCard, AlertTriangle, Scale, BookOpen } from 'lucide-react';
import LightBackground from '@/components/LightBackground';

const TermsOfService = () => {
  const lastUpdated = 'February 3, 2026';

  return (
    <>
      <Helmet>
        <title>Terms of Service – Faceless Aura Club</title>
        <meta 
          name="description" 
          content="Terms of Service for Faceless Aura Club. Review our membership terms, usage policies, and legal agreements." 
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />
        
        {/* Header */}
        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-16">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-slate-600">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Terms Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-10 space-y-10">
            
            {/* Introduction */}
            <section>
              <p className="text-slate-700 leading-relaxed">
                Welcome to Faceless Aura Club. These Terms of Service ("Terms") govern your access to and use of 
                our website <strong>facelessaura.club</strong> and all related services, content, and products 
                (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
              </p>
            </section>

            {/* Acceptance */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">1. Acceptance of Terms</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>By creating an account or using our Service, you confirm that:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>You are at least 18 years of age</li>
                  <li>You have the legal capacity to enter into a binding agreement</li>
                  <li>You agree to comply with these Terms and all applicable laws</li>
                  <li>If you are using the Service on behalf of an organization, you have authority to bind that organization</li>
                </ul>
              </div>
            </section>

            {/* Membership */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">2. Membership & Access</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p><strong>Account Registration:</strong> You must provide accurate information when creating an account. You are responsible for maintaining the security of your login credentials.</p>
                <p><strong>Membership Plans:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Monthly Subscription:</strong> $4.99/month, billed monthly, cancel anytime</li>
                  <li><strong>Lifetime Access:</strong> One-time payment of $37 for permanent access</li>
                  <li><strong>Free Trial:</strong> Limited access to introductory content</li>
                </ul>
                <p><strong>Access Rights:</strong> Your membership grants you a personal, non-transferable, non-exclusive license to access the course materials for your own educational purposes.</p>
              </div>
            </section>

            {/* Payment Terms */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">3. Payment Terms</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p><strong>Billing:</strong> All payments are processed securely through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis (for monthly plans) until you cancel.</p>
                <p><strong>Price Changes:</strong> We reserve the right to modify pricing. Existing subscribers will be notified 30 days in advance of any price increases affecting their subscription.</p>
                <p><strong>Failed Payments:</strong> If a payment fails, we may suspend access until the payment issue is resolved. We will attempt to notify you via email.</p>
                <p><strong>Taxes:</strong> Prices may be exclusive of applicable taxes. You are responsible for any taxes associated with your purchase.</p>
              </div>
            </section>

            {/* Cancellation & Refunds */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Cancellation & Refunds</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p><strong>Monthly Subscriptions:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Cancel anytime through your account dashboard or by contacting support</li>
                  <li>Access continues until the end of your current billing period</li>
                  <li>No refunds for partial months</li>
                </ul>
                <p><strong>Lifetime Access:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>7-day money-back guarantee from date of purchase</li>
                  <li>Request refunds by emailing support@facelessaura.club</li>
                  <li>Refunds processed within 5-10 business days</li>
                </ul>
                <p>
                  For full details, see our{' '}
                  <Link to="/refunds" className="text-blue-600 hover:text-blue-700 underline">
                    Refund Policy
                  </Link>.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Intellectual Property</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p><strong>Our Content:</strong> All content on the Service—including courses, videos, text, graphics, logos, and software—is the property of Faceless Aura Club or our licensors and is protected by copyright, trademark, and other intellectual property laws.</p>
                <p><strong>Restrictions:</strong> You may NOT:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Copy, reproduce, or distribute our content without permission</li>
                  <li>Share your account credentials or course access with others</li>
                  <li>Resell, sublicense, or commercially exploit any content</li>
                  <li>Remove copyright notices or watermarks from materials</li>
                  <li>Use automated tools to download or scrape content</li>
                </ul>
                <p><strong>User Content:</strong> If you submit content (e.g., in community discussions), you grant us a license to use it for operating the Service.</p>
              </div>
            </section>

            {/* Acceptable Use */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Acceptable Use</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>You agree NOT to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Violate any laws or regulations</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Impersonate others or provide false information</li>
                  <li>Interfere with the security or functionality of the Service</li>
                  <li>Use the Service for unauthorized commercial purposes</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                </ul>
                <p>We reserve the right to suspend or terminate accounts that violate these guidelines.</p>
              </div>
            </section>

            {/* Earnings Disclaimer */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-semibold text-slate-900">7. Earnings Disclaimer</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="font-medium">IMPORTANT: Results vary. There is no guarantee of income.</p>
                <p>Any income or earnings examples mentioned on our website or in our materials are illustrative only. Your results will depend on many factors including:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Your level of effort and dedication</li>
                  <li>Your prior experience and skills</li>
                  <li>Market conditions and competition</li>
                  <li>Economic factors beyond your control</li>
                </ul>
                <p>We make no representations that you will achieve similar results. Past performance is not indicative of future results.</p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">8. Limitation of Liability</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>The Service is provided "AS IS" without warranties of any kind</li>
                  <li>We do not guarantee uninterrupted or error-free access</li>
                  <li>We are not liable for any indirect, incidental, special, or consequential damages</li>
                  <li>Our total liability is limited to the amount you paid us in the 12 months preceding the claim</li>
                </ul>
              </div>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Indemnification</h2>
              <p className="text-slate-700 leading-relaxed">
                You agree to indemnify and hold harmless Faceless Aura Club, its officers, directors, employees, 
                and affiliates from any claims, damages, losses, or expenses (including legal fees) arising from 
                your use of the Service, violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Modifications to Terms</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update these Terms from time to time. We will notify you of material changes by posting 
                the updated Terms on this page and updating the "Last updated" date. Your continued use of the 
                Service after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Termination</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>We may suspend or terminate your access to the Service at any time for:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Violation of these Terms</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Non-payment of fees</li>
                  <li>Any reason at our sole discretion with reasonable notice</li>
                </ul>
                <p>Upon termination, your right to access the Service ceases immediately.</p>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Governing Law</h2>
              <p className="text-slate-700 leading-relaxed">
                These Terms are governed by and construed in accordance with the laws of the jurisdiction 
                in which Faceless Aura Club operates, without regard to conflict of law principles. Any 
                disputes shall be resolved in the courts of that jurisdiction.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">13. Contact Us</h2>
              <div className="text-slate-700 leading-relaxed space-y-2">
                <p>If you have questions about these Terms, contact us at:</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:support@facelessaura.club" className="text-blue-600 hover:text-blue-700 underline">
                    support@facelessaura.club
                  </a>
                </p>
                <p><strong>Website:</strong> facelessaura.club</p>
              </div>
            </section>

          </div>

          {/* Footer link */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
            >
              Return to Homepage
            </Link>
          </div>
        </main>
      </div>
    </>
  );
};

export default TermsOfService;
